import React, { useState, useEffect } from "react";
import validator from "validator";
import {
  Form,
  Button,
  ProgressBar,
  Alert,
  Badge,
  ListGroup,
  Spinner,
} from "react-bootstrap";

function SendMails({ gmail, appPassword }) {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedList, setFailedList] = useState([]);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API_KEY = process.env.REACT_APP_API_KEY;

  // --- Local Storage Keys ---
  const LOCAL_SUBJECT = "draft_subject";
  const LOCAL_CONTENT = "draft_content";
  //   const LOCAL_RESUME = "draft_resume"; // Base64 string

  // --- Load draft from localStorage on mount ---
  useEffect(() => {
    const savedSubject = localStorage.getItem(LOCAL_SUBJECT);
    const savedContent = localStorage.getItem(LOCAL_CONTENT);
    // const savedResume = localStorage.getItem(LOCAL_RESUME);

    if (savedSubject) setSubject(savedSubject);
    if (savedContent) setContent(savedContent);
    // if (savedResume) {
    //   // Convert Base64 string back to File object
    //   const blob = fetch(savedResume)
    //     .then((res) => res.blob())
    //     .then((b) => new File([b], "resume.pdf", { type: "application/pdf" }))
    //     .then(setResumeFile)
    //     .catch(() => {});
    // }
  }, []);

  // --- Save draft to localStorage whenever content changes ---
  useEffect(() => {
    localStorage.setItem(LOCAL_SUBJECT, subject);
  }, [subject]);

  useEffect(() => {
    localStorage.setItem(LOCAL_CONTENT, content);
  }, [content]);

  //   useEffect(() => {
  //     if (!resumeFile) {
  //       localStorage.removeItem(LOCAL_RESUME);
  //       return;
  //     }
  //     const reader = new FileReader();
  //     reader.onload = (e) => {
  //       localStorage.setItem(LOCAL_RESUME, e.target.result);
  //     };
  //     reader.readAsDataURL(resumeFile);
  //   }, [resumeFile]);

  // --- Clear draft on logout ---
  useEffect(() => {
    if (!gmail) {
      localStorage.removeItem(LOCAL_SUBJECT);
      localStorage.removeItem(LOCAL_CONTENT);
      //   localStorage.removeItem(LOCAL_RESUME);
    }
  }, [gmail]);

  const validateRecipients = (recipientsStr) => {
    const list = recipientsStr
      .split(",")
      .map((r) => r.trim())
      .filter((r) => r);

    for (let r of list) {
      if (!validator.isEmail(r)) {
        return false;
      }
    }
    return list;
  };

  const resetRecipients = () => {
    setRecipients("");
    setProgress(0);
    setTotal(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setMessage(null);
    setError(null);
    setFailedList([]);
    setSuccessCount(0);

    if (!subject || !content || !recipients) {
      setError("Please fill all required fields.");
      return;
    }

    const recipientList = validateRecipients(recipients);
    if (!recipientList) {
      setError("Please enter valid comma-separated email addresses.");
      return;
    }

    setLoading(true);
    setIsEditing(false);
    setTotal(recipientList.length);

    try {
      const formData = new FormData();
      formData.append("email", gmail);
      formData.append("password", appPassword);
      formData.append("subject", subject);
      formData.append("content", content);
      formData.append("recipients", JSON.stringify(recipientList));
      if (resumeFile) formData.append("resume", resumeFile);

      // "http://127.0.0.1:5000/send-mail",
      const res = await fetch(`${BACKEND_URL}/send-mail`, {
        headers: { "x-api-key": API_KEY },
        method: "POST",
        body: formData,
      });

      let data;
      try {
        data = await res.json();
      } catch {
        data = { error: "Invalid response from server" };
      }

      if (!res.ok) {
        setError(data.error || "Server error occurred.");
      } else {
        const results = data?.results || [];
        const successEmails = results.filter((r) => r.status === "success");
        const failedEmails = results.filter((r) => r.status === "fail");

        setSuccessCount(successEmails.length);
        setFailedList(failedEmails);
        setProgress(results.length);

        if (failedEmails.length === 0) {
          setMessage("All emails sent successfully!");
        } else {
          setMessage("Some emails failed to send.");
        }

        // Reset only recipients
        resetRecipients();
      }
    } catch (err) {
      setError("API connection failed. Is backend running?");
    }

    setLoading(false);
  };

  return (
    <div className="px-3">
      <div className="mb-3">
        <Badge bg="dark">Sending from: {gmail}</Badge>
      </div>

      <div className="mb-2">
        {!isEditing && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          >
            Edit Draft
          </Button>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="info">{message}</Alert>}

      {loading && (
        <Alert variant="dark" className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          Sending emails... Please wait while we process {total} recipient(s).
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Subject *</Form.Label>
          <Form.Control
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            disabled={loading || !isEditing}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Content *</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            disabled={loading || !isEditing}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Resume File (PDF - Optional)</Form.Label>
          <Form.Control
            type="file"
            accept=".pdf"
            onChange={(e) => setResumeFile(e.target.files[0])}
            disabled={loading}
          />
          {resumeFile && !isEditing && (
            <div>Current file: {resumeFile.name}</div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Recipient Emails *</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            required
            disabled={loading}
          />
        </Form.Group>

        <div className="d-grid mt-3">
          <Button variant="dark" type="submit" disabled={loading}>
            {loading ? "Processing..." : "Send Emails"}
          </Button>
        </div>
      </Form>

      {loading && (
        <div className="mt-4">
          <ProgressBar
            now={total ? (progress / total) * 100 : 0}
            label={`${progress} / ${total}`}
            striped
            animated
            variant="dark"
          />
        </div>
      )}

      {!loading && total > 0 && (
        <div className="mt-4">
          <Alert variant="success">Successfully Sent: {successCount}</Alert>

          {failedList.length > 0 && (
            <>
              <Alert variant="danger">Failed: {failedList.length}</Alert>
              <ListGroup>
                {failedList.map((item, index) => (
                  <ListGroup.Item key={index} variant="danger">
                    <strong>{item.email}</strong>
                    <div style={{ fontSize: "0.85rem" }}>{item.error}</div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SendMails;
